import moment from "moment";
import { FC, useState } from "react";
import {
    HiChevronDown,
    HiOutlineClipboardCheck,
    HiOutlineClock,
    HiOutlineDocumentText,
    HiOutlineDownload,
    HiOutlineLocationMarker,
    HiOutlinePrinter,
    HiOutlineXCircle,
    HiOutlineCalendar,
    HiOutlineCheck,
    HiOutlineX
} from "react-icons/hi";
import {
    PiBuildingOffice,
    PiChatCircle,
    PiTruck,
    PiUser,
    PiClock
} from 'react-icons/pi';
import { RiWeightLine } from "react-icons/ri";
import { Badge, Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import { Pickup } from "../../../../type/entities/pickup-type";
import { pickupStatuses } from "../../../../utils/SelectorMapping";
import cn from "../../../../utils/classNames";
import NearbyPickups from "./NearbyPickups";
import { PickupService } from "../../../../services/pickup/pickupService";

type PickupWithNearby = Pickup & {
    nearby?: (Pickup & { distanceKm?: number })[];
};

interface PickupCardProps {
    containerClassName?: string;
    pickup: PickupWithNearby;
    index: number;
    isNearbyOpen: boolean;
    downloadingReceipt: Record<string, boolean>;
    downloadingTicket: Record<string, boolean>;
    onCompletePickup: (pickup: Pickup) => void;
    onFailPickup: (pickup: Pickup) => void;
    onSignPickup: (pickupId: string) => void;
    onToggleNearby: (pickupId: string) => void;
    onGenerateReceipt: (pickupId: string, nameHeadquarter: string) => void;
    onGenerateTicket: (pickupId: string, nameHeadquarter: string) => void;
    onPrintTicket: (pickupId: string) => void;
    routeId?: string; // Para aceptar recogidas sugeridas
    onRefresh?: () => void; // Para refrescar la lista después de aceptar/rechazar
    onAssignPickup?: (pickup: Pickup) => void; // Para asignar pickups cercanas
}

const PickupCard: FC<PickupCardProps> = ({
    containerClassName = "",
    pickup,
    index,
    isNearbyOpen,
    downloadingReceipt,
    downloadingTicket,
    onCompletePickup,
    onFailPickup,
    onSignPickup,
    onToggleNearby,
    onGenerateReceipt,
    onGenerateTicket,
    onPrintTicket,
    routeId,
    onRefresh,
    onAssignPickup
}) => {
    const [isAccepting, setIsAccepting] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const pickupService = new PickupService();
    const getStatusColor = (status: string) => {
        const statusObj = pickupStatuses.find(s => s.value === status);
        return statusObj?.color || '#6B7280';
    };

    const getStatusLabel = (status: string) => {
        const statusObj = pickupStatuses.find(s => s.value === status);
        return statusObj?.label || status;
    };

    const getStatusGradient = (status: string) => {
        const statusObj = pickupStatuses.find(s => s.value === status);
        return statusObj?.gradientBg || 'bg-white';
    };

    const eta = pickup.pickupDate?.date
        ? moment(pickup.pickupDate.date).format('HH:mm')
        : 'Sin hora';

    // Calcular tiempo transcurrido desde la creación
    const getTimeElapsed = () => {
        if (!pickup.createdAt?.date) return null;
        const created = moment(pickup.createdAt.date);
        const now = moment();
        const duration = moment.duration(now.diff(created));
        
        if (duration.asDays() >= 1) {
            return `${Math.floor(duration.asDays())} días`;
        } else if (duration.asHours() >= 1) {
            return `${Math.floor(duration.asHours())} horas`;
        } else {
            return `${Math.floor(duration.asMinutes())} minutos`;
        }
    };

    // Formatear fecha de recogida con más detalle
    const getFormattedPickupDate = () => {
        if (!pickup.pickupDate?.date) return 'Sin fecha programada';
        const pickupMoment = moment(pickup.pickupDate.date);
        const today = moment().startOf('day');
        const tomorrow = moment().add(1, 'day').startOf('day');
        
        if (pickupMoment.isSame(today, 'day')) {
            return `Hoy ${pickupMoment.format('HH:mm')}`;
        } else if (pickupMoment.isSame(tomorrow, 'day')) {
            return `Mañana ${pickupMoment.format('HH:mm')}`;
        } else if (pickupMoment.isBefore(today)) {
            return `Vencida - ${pickupMoment.format('DD/MM/YYYY HH:mm')}`;
        } else {
            return pickupMoment.format('DD/MM/YYYY HH:mm');
        }
    };

    // Verificar si la recogida está vencida
    const isOverdue = pickup.pickupDate?.date ? moment(pickup.pickupDate.date).isBefore(moment()) : false;

    // Obtener información de productos
    const getProductsInfo = () => {
        if (pickup.pickupProducts && pickup.pickupProducts.length > 0) {
            return pickup.pickupProducts.length;
        }
        return null;
    };

    // Funciones para manejar recogidas sugeridas
    const handleAcceptSuggested = async () => {
        if (!routeId) {
            toast.error('No se puede aceptar la recogida: falta información de la ruta');
            return;
        }

        setIsAccepting(true);
        try {
            await pickupService.acceptSuggestedPickup({
                pickupId: pickup.id,
                routeId: routeId
            });
            
            toast.success('Recogida sugerida aceptada con éxito');
            onRefresh?.();
        } catch (error) {
            console.error('Error al aceptar recogida sugerida:', error);
            toast.error('Error al aceptar la recogida sugerida');
        } finally {
            setIsAccepting(false);
        }
    };

    const handleRejectSuggested = async () => {
        setIsRejecting(true);
        try {
            await pickupService.rejectSuggestedPickup({
                pickupId: pickup.id,
                rejectionReason: 'Rechazada por el conductor'
            });
            
            toast.success('Recogida sugerida rechazada');
            onRefresh?.();
        } catch (error) {
            console.error('Error al rechazar recogida sugerida:', error);
            toast.error('Error al rechazar la recogida sugerida');
        } finally {
            setIsRejecting(false);
        }
    };

    return (
        <div className={cn("p-4 hover:bg-opacity-80 transition-colors pickup-card touch-feedback", getStatusGradient(pickup.pickupStatus), containerClassName)}>
            {/* Indicador de recogida sugerida */}
            {pickup.isSuggested && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                        <HiOutlineLocationMarker className="h-4 w-4" />
                        <span className="text-sm font-medium">Recogida Sugerida</span>
                        {pickup.distanceKm && (
                            <span className="text-xs bg-amber-100 px-2 py-0.5 rounded-full">
                                {pickup.distanceKm} km
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-amber-600 mt-1">
                        {pickup.suggestionReason === 'expired' && 'Recogida expirada cercana a tu ruta'}
                        {pickup.suggestionReason === 'failed' && 'Recogida fallida cercana a tu ruta'}
                    </div>
                </div>
            )}

            {/* Indicador de recogida vencida */}
            {isOverdue && !pickup.isSuggested && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700">
                        <HiOutlineClock className="h-4 w-4" />
                        <span className="text-sm font-medium">Recogida Vencida</span>
                    </div>
                </div>
            )}
            
            <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                            <div className="font-semibold text-gray-900 flex items-center gap-2">
                                <PiBuildingOffice className="h-4 w-4 text-gray-400" />
                                {pickup.headquarter?.name}
                                {pickup.headquarter?.highSeason && (
                                    <span
                                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium
                                                   bg-amber-50 text-amber-700 border border-amber-200"
                                    >
                                        Temporada Alta
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                <HiOutlineLocationMarker className="h-3 w-3" />
                                {pickup.headquarter?.address}, {pickup.headquarter?.city}
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                            <Badge
                                variant="flat"
                                style={{ backgroundColor: `${getStatusColor(pickup.pickupStatus)}20`, color: getStatusColor(pickup.pickupStatus) }}
                                className="text-xs"
                            >
                                {getStatusLabel(pickup.pickupStatus)}
                            </Badge>
                            {getTimeElapsed() && (
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                    <PiClock className="h-3 w-3" />
                                    hace {getTimeElapsed()}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Información de fecha y hora mejorada */}
                    <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm">
                            <HiOutlineCalendar className="h-4 w-4 text-gray-500" />
                            <span className={cn(
                                "font-medium",
                                isOverdue ? "text-red-600" : "text-gray-700"
                            )}>
                                {getFormattedPickupDate()}
                            </span>
                        </div>
                        {pickup.scheduledNext && (
                            <div className="text-xs text-gray-500 mt-1">
                                Próxima programada: {moment(pickup.scheduledNext.date).format('DD/MM/YYYY')}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm grid-responsive">
                        {/*
                        <div className="flex items-center gap-2 text-gray-600">
                            <RiWeightLine className="h-4 w-4" />
                            <span>{pickup.headquarter?.drumsNumber || 0} bidones</span>
                            {pickup.pickedWeightKg && (
                                <span className="text-xs text-green-600 ml-1">
                                    ({pickup.pickedWeightKg} kg recogidos)
                                </span>
                            )}
                        </div>
                        */}
                        <div className="flex items-center gap-2 text-gray-600">
                            <RiWeightLine className="h-4 w-4" />
                            <span>
                                {pickup.headquarterAssets 
                                    ? (pickup.headquarterAssets
                                        .filter(asset => asset.type === 'CONTAINER')
                                        .reduce((acc, asset) => acc + asset.quantity, 0))
                                    : (pickup.headquarter?.drumsNumber || 0)
                                } contenedores
                            </span>
                            {pickup.pickedWeightKg && (
                                <span className="text-xs text-green-600 ml-1">
                                    ({pickup.pickedWeightKg} kg recogidos)
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                            <HiOutlineClock className="h-4 w-4" />
                            <span>ETA: {eta}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                            <PiTruck className="min-h-[15px] min-w-[15px]" />
                            <span>
                                {pickup.vehicle
                                    ? `${pickup.vehicle.brand} ${pickup.vehicle.model} (${pickup.vehicle.plateNumber})`
                                    : 'Sin vehículo asignado'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                            <PiUser className="min-h-[15px] min-w-[15px]" />
                            <span>
                                {pickup.user
                                    ? `${pickup.user.name} ${pickup.user.lastName || ''}`.trim()
                                    : 'Sin conductor asignado'}
                            </span>
                        </div>
                    </div>

                    {/* Información adicional de productos si está disponible */}
                    {getProductsInfo() && (
                        <div className="mt-2 text-xs text-gray-500">
                            Total de productos: {getProductsInfo()}
                        </div>
                    )}

                    {pickup.comments && (
                        <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                            <PiChatCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{pickup.comments}</span>
                        </div>
                    )}

                    {/* Botones de acción según estado */}
                    <div className="mt-4 flex items-center gap-2">
                        {/* Botones para recogidas sugeridas */}
                        {pickup.isSuggested && (
                            <>
                                <Tooltip content="Aceptar recogida sugerida">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 bg-green-50 border-green-500 text-green-600 hover:bg-green-100 gap-2"
                                        onClick={handleAcceptSuggested}
                                        isLoading={isAccepting}
                                        disabled={isAccepting || isRejecting}
                                        aria-label="Aceptar recogida sugerida"
                                    >
                                        <HiOutlineCheck className="h-4 w-4" />
                                        Aceptar
                                    </Button>
                                </Tooltip>
                                
                                <Tooltip content="Rechazar recogida sugerida">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 bg-red-50 border-red-500 text-red-600 hover:bg-red-100 gap-2"
                                        onClick={handleRejectSuggested}
                                        isLoading={isRejecting}
                                        disabled={isAccepting || isRejecting}
                                        aria-label="Rechazar recogida sugerida"
                                    >
                                        <HiOutlineX className="h-4 w-4" />
                                        Rechazar
                                    </Button>
                                </Tooltip>
                            </>
                        )}

                        {/* Mostrar "Completar" y "Marcar como fallida" salvo que esté completed o signature_pending */}
                        {!pickup.isSuggested && ['rescheduled', 'started_by_driver'].includes(pickup.pickupStatus) && (
                                <>
                                    <Tooltip content="Completar recogida aquí">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 bg-white border-gray-500 gap-2"
                                            onClick={() => onCompletePickup(pickup)}
                                            aria-label="Completar recogida"
                                        >
                                            <HiOutlineClipboardCheck className="h-4 w-4" />
                                            Completar
                                        </Button>
                                    </Tooltip>
                                    
                                    <Tooltip content="Marcar recogida como fallida">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 bg-white border-red-500 text-red-600 hover:bg-red-50 gap-2"
                                            onClick={() => onFailPickup(pickup)}
                                            aria-label="Marcar como fallida"
                                        >
                                            <HiOutlineXCircle className="h-4 w-4" />
                                            Marcar fallida
                                        </Button>
                                    </Tooltip>
                                </>
                            )}

                        {/* Si está pendiente de firma, mostrar botón de firmar */}
                        {pickup.pickupStatus === 'signature_pending' && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onSignPickup(pickup.id)}
                                className="w-1/4 bg-white border-gray-500 gap-2"
                            >
                                Firmar recogida
                            </Button>
                        )}

                        {!!pickup.nearby?.length && pickup.pickupStatus === 'failed' && (
                            <button
                                type="button"
                                onClick={() => onToggleNearby(pickup.id)}
                                className="ml-auto inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 bg-white border-gray-500 gap-2"
                                aria-expanded={isNearbyOpen}
                                aria-controls={`nearby-${pickup.id}`}
                            >
                                Recogidas cercanas ({pickup.nearby.length})
                                <HiChevronDown className={cn("h-4 w-4 transition-transform", isNearbyOpen && "rotate-180")} />
                            </button>
                        )}

                        {/* Acciones de descarga cuando la recogida está completada */}
                        {['completed', 'finished_by_driver'].includes(pickup.pickupStatus) && (
                            <div className="w-full mt-3">
                                {/* responsive: en móvil 1 columna; en >=sm dos botones en línea */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <Tooltip content="Descargar albarán">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full bg-white border-gray-500"
                                            isLoading={!!downloadingReceipt[pickup.id]}
                                            onClick={() => onGenerateReceipt(pickup.id, pickup.headquarter?.name || 'Sin nombre')}
                                        >
                                            <HiOutlineDocumentText className="h-4 w-4 mr-1" />
                                            Descargar albarán
                                        </Button>
                                    </Tooltip>

                                    <Tooltip content="Descargar ticket">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full bg-white border-gray-500"
                                            isLoading={!!downloadingTicket[pickup.id]}
                                            onClick={() => onGenerateTicket(pickup.id, pickup.headquarter?.name || 'Sin nombre')}
                                        >
                                            <HiOutlineDownload className="h-4 w-4 mr-1" />
                                            Descargar ticket
                                        </Button>
                                    </Tooltip>

                                    {/* Print Ticked */}
                                    <Tooltip content="Imprimir ticket">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full bg-white border-gray-500"
                                            isLoading={!!downloadingTicket[pickup.id]}
                                            onClick={() => onPrintTicket(pickup.id)}
                                        >
                                            <HiOutlinePrinter className="h-4 w-4 mr-1" />
                                            Imprimir ticket
                                        </Button>
                                    </Tooltip>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Panel 'cercanas' */}
                    {!!pickup.nearby?.length && (
                        <NearbyPickups
                            pickupId={pickup.id}
                            nearbyPickups={pickup.nearby}
                            isOpen={isNearbyOpen}
                            onAssignPickup={onAssignPickup || (() => {})}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PickupCard;